import { parseSavedSearchUrlToSummary } from '../saved-searches/parse-saved-search-summary';
import { buildSavedSearchName } from '../saved-searches/describe-saved-search';
import type { SearchEntry } from '../../redux/features/savedSearchesSlice';
import type { SearchPanelRecentSearch } from '../../components/search-panel/types';

/**
 * Search panel spec step 4: maps the Redux `savedSearches` list (recorded searches, most recent
 * first per the slice's own ordering) to SearchPanel's `recentSearches` prop. No dependency on
 * Redux itself: this file takes a plain array, so hosts (Autocomplete.tsx, SearchLauncher.tsx)
 * read the slice themselves and pass the result in, keeping SearchPanel's own files Redux-free.
 * Labels reuse the same "human-readable name from a saved search URL" building block
 * `/tools/searches` itself uses (parseSavedSearchUrlToSummary), but NOT that page's own
 * `buildSavedSearchName`: it title-cases its result, which is right for that page's formal list
 * but wrong here (coordinator review round 3, bug 4) when the entry is a plain free-text query:
 * a query typed as "reactsenior" should reappear exactly "as saved", not as "Reactsenior". A
 * filter-only search (no free text) has no raw text to fall back to, so it still goes through
 * `buildSavedSearchName`'s constructed, and appropriately capitalized, sentence (e.g. "Jobs at
 * Nokia").
 */
export function mapSavedSearchesToRecent(entries: SearchEntry[], limit = 2): SearchPanelRecentSearch[] {
  return entries
    .filter((entry) => typeof entry.url === 'string' && entry.url.trim().length > 0)
    .slice(0, limit)
    .map((entry) => {
      const summary = parseSavedSearchUrlToSummary(entry.url);
      const label = summary.query?.trim() ? summary.query.trim() : buildSavedSearchName(summary, entry.url);
      return { label, url: entry.url };
    });
}
